import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

/**
 * A drop-in for `axios` in the services that call the BFF with raw axios (XBIT-4539).
 *
 * The service answers an upstream failure as 422 `UPSTREAM_ERROR` / 502 `UPSTREAM_UNAVAILABLE` with an
 * envelope `{code, message, upstreamCode?}`. Plain axios would show "Request failed with status code 422"
 * for it. Here, on a non-2xx whose body carries a `message`, the same `AxiosError` is rethrown with
 * `message` set to the envelope's message, plus `apiCode` and `upstreamCode`. Being the same object,
 * `instanceof`, `err.response` and every `err.response?.data?.message` reader keep working.
 */

function describeFailure(err: any): void {
  const data = err?.response?.data;
  if (!data || typeof data !== 'object') return;
  const message = Array.isArray(data.message) ? data.message.filter((m: unknown) => typeof m === 'string').join(', ') : data.message;
  if (typeof message !== 'string' || message.length === 0) return;
  err.message = message;
  err.apiCode = data.code;
  err.upstreamCode = data.upstreamCode;
}

async function run<T>(call: () => Promise<AxiosResponse<T>>): Promise<AxiosResponse<T>> {
  try {
    return await call();
  } catch (err) {
    describeFailure(err);
    throw err;
  }
}

const upstreamAwareAxios = {
  get: <T = any>(url: string, config?: AxiosRequestConfig) => run<T>(() => axios.get<T>(url, config)),
  delete: <T = any>(url: string, config?: AxiosRequestConfig) => run<T>(() => axios.delete<T>(url, config)),
  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) => run<T>(() => axios.post<T>(url, data, config)),
  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) => run<T>(() => axios.put<T>(url, data, config)),
  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) => run<T>(() => axios.patch<T>(url, data, config)),
};

export default upstreamAwareAxios;

/** True when the BFF relayed the upstream's "record not found" refusal (a 422 now, a 200 with `errors` before). */
export function isUpstreamNotFound(err: any): boolean {
  return err?.response?.data?.message === 'record not found';
}
